import { useMemo, useState } from "react";
import type { SortingState } from "@tanstack/react-table";

import EmptyState from "@/components/EmptyState";
import Table, { buildTagColumn } from "@/components/Table";
import type { TagType } from "@/components/Tag/Tag";
import { toFormat } from "@/lib/dateHelpers";
import { UNTITLED_CAREER_PLAN_TITLE } from "./_CareerPlanTitle";
import { CAREER_PLAN_STATUS } from "./careerPlanDocument";
import type { HubCareerPlan } from "./careerPlanHubSummaries";

type CareerPlanListRow = HubCareerPlan & {
  displayTitle: string;
  lastModified: string;
};

function planDisplayTitle(title: string | null) {
  return title?.trim() || UNTITLED_CAREER_PLAN_TITLE;
}

function planStatusLabel(status: string) {
  switch (status) {
    case CAREER_PLAN_STATUS.UNPUBLISHED:
      return "Unpublished";
    case CAREER_PLAN_STATUS.PUBLISHED:
      return "Published";
    case CAREER_PLAN_STATUS.COMPLETED:
      return "Completed";
    case CAREER_PLAN_STATUS.DRAFT:
      return "Draft";
    default:
      return "Draft";
  }
}

function planStatusTagType(status: string): TagType {
  switch (status) {
    case CAREER_PLAN_STATUS.UNPUBLISHED:
      return "warn";
    case CAREER_PLAN_STATUS.PUBLISHED:
      return "success";
    case CAREER_PLAN_STATUS.COMPLETED:
      return "information";
    case CAREER_PLAN_STATUS.DRAFT:
      return "surface";
    default:
      return "surface";
  }
}

function planStatusSortRank(status: string): number {
  switch (status) {
    case CAREER_PLAN_STATUS.PUBLISHED:
      return 0;
    case CAREER_PLAN_STATUS.UNPUBLISHED:
      return 1;
    case CAREER_PLAN_STATUS.DRAFT:
      return 2;
    case CAREER_PLAN_STATUS.COMPLETED:
      return 3;
    default:
      return 2;
  }
}

export default function CareerPlanListTable({
  plans,
  pgaId,
  loading
}: {
  plans: HubCareerPlan[];
  pgaId: string;
  loading: boolean;
}) {
  const [sortingState, setSortingState] = useState<SortingState>([
    { id: "status", desc: false },
    { id: "updatedAt", desc: true }
  ]);
  const tableData = useMemo<CareerPlanListRow[]>(
    () =>
      plans.map((plan) => ({
        ...plan,
        displayTitle: planDisplayTitle(plan.title),
        lastModified: plan.updatedAt ? toFormat(plan.updatedAt) || "—" : "—"
      })),
    [plans]
  );

  return (
    <Table
      data={tableData}
      loading={loading}
      sortingState={sortingState}
      onSortingChange={setSortingState}
      getRowLink={(row) => ({
        to: `/people/${pgaId}/career/career-profile/plans/${row.original.id}`
      })}
      columns={[
        {
          accessorKey: "displayTitle",
          header: "Title",
          size: 360,
          enableSorting: false,
          meta: { headerClassName: "hide-header-in-column" }
        },
        {
          accessorKey: "updatedAt",
          header: "Last Modified",
          size: 160,
          meta: { sortType: "date" },
          cell: ({ row }) => row.original.lastModified
        },
        {
          ...buildTagColumn<CareerPlanListRow>({
            accessorKey: "status",
            header: "Status",
            size: 160,
            enableSorting: true,
            getTagLabel: (row) => planStatusLabel(row.status),
            getTagType: (row) => planStatusTagType(row.status)
          }),
          sortingFn: (rowA, rowB) => planStatusSortRank(rowA.original.status) - planStatusSortRank(rowB.original.status)
        }
      ]}
      renderNoDataEmptyState={() => (
        <EmptyState
          iconName="rubric"
          title="No Career Plans"
          caption="This person has not been provided a career plan yet."
        />
      )}
      data-testid="career-plan-table"
    />
  );
}
